import React, { Fragment, useMemo } from 'react';
import _ from 'lodash';

import AnomalyDescription from '../report-items/anomalyDescription';
import AnomalyRemarks from '../report-items/anomalyRemarks';
import Conditional from '../../../common/conditional';
import CustomSection from '../../custom-general/custom-section';
import PDFTablePage from '../../../themed/pdf/pdfTablePage';
import PCNLRiskAssessmentTable from '../../custom-pcnl/pcnl-risk-assessment/table';

import { getReportDetails } from '../../../../store/reportDetails';
import { getAnomalyId, getChunkedData, getPCNLAnomalyStyle, matchPhrases } from '../../../../utils/utilities';
import { Column, PCNLAnomalyItem, PCNLItemModel, Project } from '../../../../utils/models';
import { TABLE_ITEM_COUNT_LIMITS } from '../../../../utils/constants';
import { ExportParams } from '../../../blocks/pdf-export';
import { useAppSelector } from '../../../../store/configureStore';

import classNames from './style.module.scss';


interface Props {
    projectDetails: Project;
    items: PCNLItemModel[];
    exportParams: ExportParams;
    title?: string;
}

const PCNLAnomalyRecommendation: React.FC<Props> = ({ exportParams, projectDetails, items, title = 'Anomaly & Recommendation' }) => {
    const { itemCountPerPage } = useAppSelector(getReportDetails);

    const mappedPCNLItems = useMemo(() => {
        return items.flatMap((item) => {
            if (item.anomalyItems.length > 0) {
                return item.anomalyItems.map((anomaly, anomalyIndex) => ({
                    id: anomaly.id,
                    pcnlId: getAnomalyId(item.id, anomalyIndex + 1), 
                    pcnlItemName: item.pcnlItemName,
                    riskFactor: anomaly.riskFactor,
                    description: anomaly.description,
                    recommendation: anomaly.recommendation,
                }));
            }

            return {
                id: 0,
                pcnlId: item.id.toString(), 
                pcnlItemName: item.pcnlItemName,
                riskFactor: '',
                description: [],
                recommendation: [],
            };
        });
    }, [items]);

    const anomalyItemColumns: Column[] = useMemo(() => {
        return [
            {
                path: 'pcnlId',
                label: 'Sr. No',
            },
            {
                path: 'pcnlItemName',
                label: 'PCNL Item Name'
            },
            {
                path: 'riskFactor',
                label: 'Risk Factor',
                style: (item: PCNLAnomalyItem) => getPCNLAnomalyStyle(item)
            },
            {
                path: 'description',
                label: 'Anomaly Details',
                content: (item: PCNLAnomalyItem) => (
                    <>
                        {item.description.length > 0 ? <AnomalyDescription anomaly={item} /> : 'N/A'}
                    </>
                ),
                style: () => ({ textAlign: 'left' })
            },
            {
                path: 'recommendation',
                label: 'Recommendation',
                content: (anomaly: PCNLAnomalyItem) => (
                    <>
                        {anomaly.recommendation.length > 0 ? <AnomalyRemarks key={anomaly.id} anomaly={anomaly} /> : 'N/A'}
                    </>
                )
            },
        ];
    }, []);

    const riskAssessmentItems = useMemo(() => {
        return items.filter((item) => {
            const isRiskAssessment = matchPhrases(_.get(item, 'pcnlItemName'), 'risk assessment');
            const hasAddedAssessments = item.riskAssessment && item.riskAssessment.length > 0;

            return isRiskAssessment && hasAddedAssessments;
        });
    }, [items]);
    
    return ( 
       <>
            {getChunkedData(mappedPCNLItems, itemCountPerPage).map((chunk, index) => (
                <Fragment key={index}>
                    <CustomSection pageBreakClass={exportParams.pdfBreakClass} sectionTitle={title}>
                        <div className={classNames.anomalyRecommendation}>
                            <Conditional isVisible={index === 0}>
                                <div className={classNames.anomalyRecommendationFacility}>
                                    <div className={classNames.anomalyRecommendationFacilityHeader}>Facility</div>  
                                    <div className={classNames.anomalyRecommendationFacilityValue}>{projectDetails.facility.facilityName}</div>  
                                </div>
                            
                                <div className="mt-4">
                                    <div className={classNames.anomalyRecommendationDetails}>
                                        <div className={classNames.anomalyRecommendationDetailsItem}>
                                            <div className={classNames.anomalyRecommendationDetailsItemHeader}>Tag</div>  
                                            <div className={classNames.anomalyRecommendationDetailsItemValue}>{projectDetails.equipmentTag}</div>  
                                        </div>
                                        <div className={classNames.anomalyRecommendationDetailsItem}>
                                            <div className={classNames.anomalyRecommendationDetailsItemHeader}>Location</div>  
                                            <div className={classNames.anomalyRecommendationDetailsItemValue}>{projectDetails.location}</div>  
                                        </div>
                                        <div className={classNames.anomalyRecommendationDetailsItem}>
                                            <div className={classNames.anomalyRecommendationDetailsItemHeader}>Module</div>  
                                            <div className={classNames.anomalyRecommendationDetailsItemValue}>{projectDetails.module}</div>  
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-2">
                                    <div className={classNames.anomalyRecommendationDetails}>
                                        <div className={classNames.anomalyRecommendationDetailsItem}>
                                            <div className={classNames.anomalyRecommendationDetailsItemHeader}>Inspection Type</div>  
                                            <div className={classNames.anomalyRecommendationDetailsItemValue}>{projectDetails.inspectionTypeName}</div>  
                                        </div>
                                    </div>
                                </div>

                                <div className={classNames.anomalyRecommendationBreak}></div>
                            </Conditional>

                            <PDFTablePage data={chunk} columns={anomalyItemColumns} />
                        </div>
                    </CustomSection>
                </Fragment>
            ))}
            
            <Conditional isVisible={riskAssessmentItems.length > 0}>
                {riskAssessmentItems.map((item, index) => {
                    if (!item.riskAssessment ) return null;

                    return (
                        <Fragment key={index}>
                            {getChunkedData(item.riskAssessment, TABLE_ITEM_COUNT_LIMITS.PCNL_RISK_ASSESSMENT - 2).map((findings, riskIndex) => (
                                <CustomSection 
                                    key={riskIndex} 
                                    pageBreakClass={exportParams.pdfBreakClass}
                                    sectionTitle='Risk Assessment Findings'
                                    sectionSubtitle={_.get(item, 'pcnlItemName')}
                                >
                                    <div className={classNames.anomalyRecommendation}>
                                        <Conditional isVisible={index === 0}>
                                            <div className={classNames.anomalyRecommendationFacility}>
                                                <div className={classNames.anomalyRecommendationFacilityHeader}>Facility</div>  
                                                <div className={classNames.anomalyRecommendationFacilityValue}>{projectDetails.facility.facilityName}</div>  
                                            </div>
                                        
                                            <div className="mt-4">
                                                <div className={classNames.anomalyRecommendationDetails}>
                                                    <div className={classNames.anomalyRecommendationDetailsItem}>
                                                        <div className={classNames.anomalyRecommendationDetailsItemHeader}>Tag</div>  
                                                        <div className={classNames.anomalyRecommendationDetailsItemValue}>{projectDetails.equipmentTag}</div>  
                                                    </div>
                                                    <div className={classNames.anomalyRecommendationDetailsItem}>
                                                        <div className={classNames.anomalyRecommendationDetailsItemHeader}>Location</div>  
                                                        <div className={classNames.anomalyRecommendationDetailsItemValue}>{projectDetails.location}</div>  
                                                    </div>
                                                    <div className={classNames.anomalyRecommendationDetailsItem}>
                                                        <div className={classNames.anomalyRecommendationDetailsItemHeader}>Module</div>  
                                                        <div className={classNames.anomalyRecommendationDetailsItemValue}>{projectDetails.module}</div>  
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mt-2">
                                                <div className={classNames.anomalyRecommendationDetails}>
                                                    <div className={classNames.anomalyRecommendationDetailsItem}>
                                                        <div className={classNames.anomalyRecommendationDetailsItemHeader}>Inspection Type</div>  
                                                        <div className={classNames.anomalyRecommendationDetailsItemValue}>{projectDetails.inspectionTypeName}</div>  
                                                    </div>
                                                </div>
                                            </div>

                                            <div className={classNames.anomalyRecommendationBreak}></div>
                                        </Conditional>

                                        <PCNLRiskAssessmentTable data={findings} />
                                    </div>
                                </CustomSection>
                            ))}
                        </Fragment>
                    );
                })}
            </Conditional>
       </>
     );
};

export default PCNLAnomalyRecommendation;